"use client";

import DOMPurify from "dompurify";
import { Check, CircleChevronRight, Loader2 } from "lucide-react";

import { Button } from "@repo/ui/components/ui/button";
import { useFormatPrice } from "@repo/ui/Hooks/useFormatPrice";
import { usePassengerName } from "@repo/ui/Hooks/usePassengerName";
import { cn } from "@repo/ui/lib/utils";
import type {
  CommonInfoSection,
  PetOnBoardSection,
  PriorityBoardingSection,
} from "@repo/ui/types/cms";

export type PassengerItemCardProps = {
  passengerKey: string;
  isAdded: boolean;
  price: number | string;
  onChangeAction: (
    passengerKey: string,
    price: number | string,
    ssrKey?: string,
    ssrCode?: string,
  ) => void;
  index: number;
  disabled?: boolean;
  cmsData?: PetOnBoardSection | PriorityBoardingSection;
  commonInfo?: CommonInfoSection;
  ssrKey?: string;
  isInCludedInBundle?: boolean;
  ssrCode?: string;
  loading?: boolean;
  isSoldOut?: boolean;
};

export function PassengerItemCard({
  passengerKey,
  isAdded,
  price,
  onChangeAction,
  index,
  disabled = false,
  cmsData,
  commonInfo,
  ssrKey,
  isInCludedInBundle,
  ssrCode,
  loading = false,
  isSoldOut = false,
}: Readonly<PassengerItemCardProps>) {
  const formatPrice = useFormatPrice();
  const getPassengerName = usePassengerName();

  const passengerName =
    getPassengerName(passengerKey) ||
    `${commonInfo?.passengerLabel ?? "Pasajero"} ${(index ?? 0) + 1}`;

  const renderIcon = () => {
    if (loading) return <Loader2 className="inline ml-1 animate-spin" />;
    if (isInCludedInBundle || isAdded) return <Check className="inline ml-1" />;
    return <CircleChevronRight className="inline ml-1" />;
  };

  return (
    <div className="w-full" data-testid={`passenger-item-${passengerKey}-section`}>
      <div
        className={cn(
          "flex w-full items-center justify-between gap-4 rounded-xl border p-4 hover:shadow-xl transition-all ease-in-out duration-700 bg-white",
        )}
      >
        <div className="flex flex-col justify-center">
          {isSoldOut && (
            <span
              className="text-xs font-semibold text-brand-red mb-1 leading-tight"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(
                  (cmsData as PetOnBoardSection)?.soldOut?.message ||
                    "La capacidad de mascotas para este vuelo ya está completa.",
                ),
              }}
            />
          )}
          <div className="text-sm font-medium wrap-break-word text-brand-blue">
            <span className="font-semibold">
              {passengerName} {formatPrice(price)}
            </span>
          </div>
        </div>

        <Button
          onClick={() =>
            !disabled && !loading && onChangeAction(passengerKey, price, ssrKey, ssrCode)
          }
          disabled={disabled || loading}
          className={cn(
            "whitespace-nowrap",
            isAdded
              ? "bg-brand-cyan text-white hover:bg-cyan-600!"
              : "hover:bg-brand-red bg-[#22457A]",
            disabled ? "cursor-not-allowed bg-brand-cyan!" : "",
          )}
          data-testid={`passenger-item-${passengerKey}-lbl`}
        >
          <>
            {isInCludedInBundle || isAdded
              ? (cmsData?.addedButtonText ?? "Added")
              : (cmsData?.addButtonText ?? "Add")}
            {renderIcon()}
          </>
        </Button>
      </div>
    </div>
  );
}
